import { Bell, ChatCircle, MagnifyingGlass, Phone, SlidersHorizontal, SquaresFour, Timer, type Icon } from '@phosphor-icons/react';
import type { DeckPage, IslandMode } from './IslandProvider';

/** Box size (border-box) per island mode. `deck.h` is a fallback — the live deck
 *  height comes from {@link DECK_PAGE_HEIGHT} for the active page. */
export const SIZE: Record<IslandMode, { w: number; h: number; r: number }> = {
  idle:  { w: 212, h: 38,  r: 19 },
  live:  { w: 308, h: 44,  r: 22 },
  toast: { w: 372, h: 60,  r: 24 },
  // Anrufkarte (Handoff Pille §3): Kopf mit 52-px-Avatar, Status, Name, Rolle/Nummer.
  // Zusammenhang, Auswahlliste und Hinweis bei unbekannter Nummer wachsen dazu —
  // siehe computeIslandSize in island-size.ts.
  anruf: { w: 400, h: 96, r: 26 },
  deck:  { w: 620, h: 452, r: 30 },
  // Formular (Handoff Pillen-Formulare §2): h ist die OBERGRENZE – die Pille wächst mit
  // der gemessenen Inhaltshöhe bis 760 px (viewport-gedeckelt), siehe computeIslandSize.
  form:  { w: 480, h: 760, r: 30 },
};

/** Ruhende Pille mit Bereichsname bzw. „N neu“ daneben (Prototyp: 236 statt 212). */
export const IDLE_MIT_TEXT_W = 236;

/** Ruhende Pille mit laufendem Ticket-Timer: „● 0:12:34 · Ticket T-0192“ + Stopp (Handoff Tickets §7). */
export const IDLE_TIMER_W = 268;

/** Size of the idle pill while hovered (the hover-peek state). */
export const SIZE_PEEK = { w: 272, h: 40, r: 20 };

/** Anrufkarte: Trennlinie + Abstand über dem Zusammenhang-Block (einmal). */
export const ANRUF_KONTEXT_TRENNER_H = 10;

/** Anrufkarte bei unbekannter Nummer: Zeile „Diese Nummer ist keinem Kontakt zugeordnet.“ */
export const ANRUF_UNBEKANNT_H = 24;

/** Höhe der Anrufkarte, wenn eine Auswahlliste bei mehrdeutigen Nummern
 *  angezeigt wird: Kopfzeile + Label + mind. zwei Kandidatenknöpfe (je 44px,
 *  tap-44). Die Liste selbst ist zusätzlich mit max-h + overflow-y-auto
 *  abgesichert, falls einmal mehr Kandidaten auflaufen. Ohne Auswahlliste
 *  bleibt es bei {@link SIZE}.anruf.h. */
export const SIZE_ANRUF_AUSWAHL_H = 214;

/** Höhe je NICHT-Ticket-Zeile im Zusammenhang-Block der Anrufkarte (Objektzeile,
 *  "+ N weitere"-Zeile, Fehlerzeile) — reiner Text/Icon ohne tap-44-Fläche, samt
 *  seinem gap-1-Abstand zur nächsten Zeile. Die Karte wächst nur um so viel, wie
 *  tatsächlich Inhalt da ist, statt pauschal auf eine feste Kontext-Höhe zu
 *  springen. Siehe computeIslandSize in island-size.ts. */
export const ANRUF_KONTEXT_ZEILE_H = 22;

/** Höhe je Ticket-Zeile im Zusammenhang-Block: die Zeilen sind Buttons mit
 *  `tap-44` (min-height: 44px, siehe index.html, für ein antippbares Ziel) —
 *  nie kleiner als das, plus gap-1-Abstand. War vorher fälschlich mit derselben
 *  Konstante wie Text-Zeilen verrechnet (Faktor zwei zu klein), wodurch die
 *  unterste Ticketzeile bei drei Vorgängen stumm abgeschnitten wurde — die
 *  Ticket-Liste ist deshalb zusätzlich mit max-h + overflow-y-auto abgesichert
 *  (AnrufContent.tsx), falls die Schätzung hier doch mal knapp wird. */
export const ANRUF_KONTEXT_TICKET_ZEILE_H = 48;

/** The deck morphs height per page — controls is short (search + filter rows),
 *  the rest want the full height for their lists/grids. */
export const DECK_PAGE_HEIGHT: Record<DeckPage, number> = {
  controls: 168,
  notifications: 452,
  chat: 452,
  zeit: 400,
  telefon: 452,
  search: 452,
  widgets: 452,
};

/** Per-page deck WIDTH override (Pages not listed → {@link SIZE}.deck.w).
 *  Sicher, seit das Deck nur die aktive Seite rendert (kein Carousel-Strip):
 *  die Pille morpht die Breite je aktiver Seite. Suche braucht mehr Raum für
 *  die Trefferliste; Controls bleibt schmal. */
export const DECK_PAGE_WIDTH: Partial<Record<DeckPage, number>> = {
  search: 820,
  chat: 720,
  widgets: 640,
};

/** Height of the deck header (page label · dots · close button). When the
 *  controls page reports a measured content height, the pill needs room
 *  for the header on top of it. */
export const DECK_HEADER_HEIGHT = 64;

/** iOS-like spring easing for the morph (slight overshoot). */
export const SPRING = 'cubic-bezier(0.34, 1.4, 0.64, 1)';
/** Smoother easing for the horizontal deck slide. */
export const SLIDE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

export const HOVER_OPEN_MS = 160;
export const HOVER_CLOSE_MS = 260;
/** Fixed throttle between swipe-driven page changes (never extended by events). */
export const SWIPE_THROTTLE_MS = 520;

/** `kurz` ist die Beschriftung der Seiten-Tabs in der Deck-Kopfzeile — bewusst
 *  knapp, damit alle vier Tabs nebeneinander passen. `label` bleibt der volle
 *  Name (Tooltip, aria-label, Platzhalterseite). */
export const PAGE_META: Record<DeckPage, { label: string; kurz: string; icon: Icon }> = {
  controls:      { label: 'Steuerung',          kurz: 'Steuerung', icon: SlidersHorizontal },
  notifications: { label: 'Benachrichtigungen', kurz: 'Meldungen', icon: Bell },
  chat:          { label: 'Team Chat',           kurz: 'Team Chat', icon: ChatCircle },
  zeit:          { label: 'Zeiterfassung',       kurz: 'Zeit',      icon: Timer },
  telefon:       { label: 'Telefon',             kurz: 'Telefon',   icon: Phone },
  search:        { label: 'Suche & Befehle',    kurz: 'Suche',     icon: MagnifyingGlass },
  widgets:       { label: 'Widgets',            kurz: 'Widgets',   icon: SquaresFour },
};
